import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Plus, Upload, Eye, Trash2, Lock, Unlock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, TipoBadge, ClabeCopy } from "@/components/common";
import { KycReadOnly } from "@/components/KycForm";
import { TxTable } from "@/components/TxTable";
import { StatementForm } from "@/components/StatementForm";
import { SECTIONS, DOC_TYPES } from "@/lib/kycSchema";
import { useApi, errMsg, downloadBlob } from "@/lib/api";
import { money, dateTime, KYC_LABEL } from "@/lib/format";

const Card = ({ title, children, action }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-6">
    <div className="mb-4 flex items-center justify-between gap-3"><h3 className="text-lg font-semibold text-slate-900">{title}</h3>{action}</div>
    {children}
  </div>
);

const StatusPanel = ({ client, onSaved }) => {
  const api = useApi();
  const [st, setSt] = useState(client.kyc_status);
  const [nota, setNota] = useState("");
  const save = async () => {
    try { onSaved((await api.patch(`/clients/${client.id}/status`, { kyc_status: st, nota })).data); setNota(""); toast.success("Estatus KYC actualizado"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Revisión KYC">
      <div className="space-y-3">
        <Select value={st} onValueChange={setSt}><SelectTrigger data-testid="kyc-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(KYC_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
        <Textarea placeholder="Nota (opcional)" value={nota} onChange={(e) => setNota(e.target.value)} data-testid="kyc-status-note" />
        <Button className="w-full bg-slate-900 hover:bg-slate-800" onClick={save} data-testid="kyc-status-save">Guardar estatus</Button>
      </div>
      <ul className="mt-6 space-y-3 border-t border-slate-100 pt-4">
        {[...(client.historial || [])].reverse().map((h, i) => (
          <li key={i} className="text-xs"><p className="font-medium text-slate-800">{h.accion}</p><p className="text-slate-500">{dateTime(h.fecha)} · {h.por}</p>{h.nota && <p className="mt-0.5 text-slate-600">{h.nota}</p>}</li>
        ))}
      </ul>
    </Card>
  );
};

const Accounts = ({ client, accounts, reload }) => {
  const api = useApi();
  const [alias, setAlias] = useState("");
  const open = async () => {
    try { await api.post(`/clients/${client.id}/accounts`, { alias: alias || "Cuenta principal" }); setAlias(""); toast.success("Cuenta abierta y CLABE asignada"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (a) => {
    try { await api.patch(`/accounts/${a.id}/status`, { estado: a.estado === "activa" ? "bloqueada" : "activa" }); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Cuentas STP" action={
      <div className="flex gap-2"><Input className="h-9 w-44" placeholder="Alias de la cuenta" value={alias} onChange={(e) => setAlias(e.target.value)} data-testid="account-alias-input" />
        <Button size="sm" className="bg-teal-600 hover:bg-teal-700" disabled={client.kyc_status !== "aprobado"} onClick={open} data-testid="open-account-btn"><Plus className="mr-1 h-4 w-4" />Abrir cuenta</Button></div>}>
      {client.kyc_status !== "aprobado" && <p className="mb-4 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">Para abrir cuentas el KYC del cliente debe estar aprobado.</p>}
      {accounts.length === 0 && <p className="text-sm text-slate-500">Sin cuentas abiertas.</p>}
      <div className="grid gap-4 md:grid-cols-2">
        {accounts.map((a) => (
          <div key={a.id} className="rounded-xl border border-slate-200 bg-gradient-to-br from-white to-teal-50/50 p-5" data-testid={`account-card-${a.id}`}>
            <div className="flex items-center justify-between"><p className="text-sm font-medium text-slate-700">{a.alias}</p><StatusBadge status={a.estado} label={a.estado} /></div>
            <p className="mt-4 text-xs text-slate-500">CLABE interbancaria</p>
            <ClabeCopy clabe={a.clabe} testId={`account-clabe-${a.id}`} />
            <p className="mt-4 text-xs text-slate-500">Saldo disponible</p>
            <p className="font-display text-2xl font-semibold text-slate-900" data-testid={`account-saldo-${a.id}`}>{money(a.saldo)}</p>
            <Button size="sm" variant="ghost" className="mt-3 px-0 text-slate-600" onClick={() => toggle(a)} data-testid={`account-toggle-${a.id}`}>
              {a.estado === "activa" ? <><Lock className="mr-1 h-3.5 w-3.5" />Bloquear</> : <><Unlock className="mr-1 h-3.5 w-3.5" />Activar</>}
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
};

const Documents = ({ client }) => {
  const api = useApi();
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState(DOC_TYPES[client.tipo][0]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/clients/${client.id}/documents`).then((r) => setDocs(r.data)), [api, client.id]);
  useEffect(() => { load(); }, [load]);
  const upload = async (file) => {
    const fd = new FormData(); fd.append("doc_type", docType); fd.append("file", file);
    setBusy(true);
    try { await api.post(`/clients/${client.id}/documents`, fd); toast.success("Documento cargado"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (d) => { await api.delete(`/documents/${d.id}`); load(); };
  return (
    <Card title="Documentos KYC">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <Select value={docType} onValueChange={setDocType}><SelectTrigger className="sm:w-80" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>{DOC_TYPES[client.tipo].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
        <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700">
          <Upload className="h-4 w-4" />{busy ? "Subiendo…" : "Subir documento"}
          <input type="file" className="hidden" accept="application/pdf,image/*" data-testid="doc-upload-input" onChange={(e) => { if (e.target.files[0]) upload(e.target.files[0]); e.target.value = ""; }} />
        </label>
      </div>
      {docs.length === 0 && <p className="text-sm text-slate-500">Sin documentos.</p>}
      <ul className="divide-y divide-slate-100">
        {docs.map((d) => (
          <li key={d.id} className="flex items-center justify-between gap-3 py-3" data-testid={`doc-row-${d.id}`}>
            <div className="min-w-0"><p className="text-sm font-medium text-slate-900">{d.doc_type}</p><p className="truncate text-xs text-slate-500">{d.original_filename} · {(d.size / 1024).toFixed(0)} KB · {dateTime(d.created_at)}</p></div>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" onClick={() => downloadBlob(api, `/documents/${d.id}/download`, d.original_filename, true)} data-testid={`doc-view-${d.id}`}><Eye className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove(d)} data-testid={`doc-delete-${d.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
};

export default function ClientDetail() {
  const { id } = useParams();
  const api = useApi();
  const [client, setClient] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const loadAccounts = useCallback(() => api.get("/accounts", { params: { client_id: id } }).then((r) => setAccounts(r.data)), [api, id]);
  useEffect(() => { api.get(`/clients/${id}`).then((r) => setClient(r.data)); loadAccounts(); }, [api, id, loadAccounts]);
  if (!client) return <div className="h-64 animate-pulse rounded-2xl bg-slate-100" />;
  const saldo = accounts.reduce((s, a) => s + a.saldo, 0);
  const sections = Object.values(SECTIONS[client.tipo]);

  return (
    <>
      <div className="rise flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-6 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2"><TipoBadge tipo={client.tipo} /><StatusBadge status={client.kyc_status} label={KYC_LABEL[client.kyc_status]} testId="client-kyc-badge" /></div>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-900" data-testid="client-name">{client.nombre}</h1>
          <p className="mt-1 font-mono text-sm text-slate-500">{client.clave && <span className="mr-2 rounded bg-slate-100 px-1.5 py-0.5 text-slate-700" data-testid="client-clave">{client.clave}</span>}RFC {client.rfc || "—"}{client.email ? ` · ${client.email}` : ""}</p>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right"><p className="text-xs text-slate-500">Saldo total</p><p className="font-display text-2xl font-semibold text-slate-900">{money(saldo)}</p></div>
          <Button asChild variant="outline" data-testid="edit-client-btn"><Link to={`/clientes/${id}/editar`}><Pencil className="mr-2 h-4 w-4" />Editar expediente</Link></Button>
        </div>
      </div>
      <Tabs defaultValue="cuentas">
        <TabsList className="flex h-auto flex-wrap justify-start bg-white p-1 shadow-sm">
          {[["cuentas", "Cuentas"], ["movimientos", "Movimientos"], ["estados", "Estados de cuenta"], ["expediente", "Expediente KYC"], ["documentos", "Documentos"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="cuentas" className="mt-6 grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2"><Accounts client={client} accounts={accounts} reload={loadAccounts} /></div>
          <StatusPanel client={client} onSaved={setClient} />
        </TabsContent>
        <TabsContent value="movimientos" className="mt-6 space-y-4">
          <Button variant="outline" data-testid="client-receipts-zip-btn" onClick={() => downloadBlob(api, `/clients/${id}/receipts.zip`, `Comprobantes_${client.clave || id}.zip`).catch((e) => toast.error(e?.response?.status === 404 ? "Sin movimientos" : errMsg(e)))}>
            <Upload className="mr-2 h-4 w-4 rotate-180" />Todos los comprobantes del cliente (ZIP)
          </Button>
          <TxTable params={{ client_id: id }} showClient={false} />
        </TabsContent>
        <TabsContent value="estados" className="mt-6"><Card title="Generar estado de cuenta"><StatementForm accounts={accounts} /></Card></TabsContent>
        <TabsContent value="expediente" className="mt-6 space-y-4">{sections.map((s) => <KycReadOnly key={s.key} section={s} value={client.kyc?.[s.key]} />)}</TabsContent>
        <TabsContent value="documentos" className="mt-6"><Documents client={client} /></TabsContent>
      </Tabs>
    </>
  );
}
